import { loadStripe } from '@stripe/stripe-js/pure';
import {
  ConfirmationToken,
  StripeElementLocale,
  StripeError,
} from '@stripe/stripe-js';
import { IAction, IBaseAction } from '../useAction';
import { IReloadAction } from './useReloadAction';
import useOverlay from '../useOverlay';
import useLanguage from '../useLanguage';
import request from '../../utils/request';
import { logInfo, logWarning } from '../../utils/remoteLogger';

const LOG_TAG = 'useManageStripeAction';

const logSaveSuccess = (stage: string) => {
  logInfo(LOG_TAG, `Save card succeeded: ${stage}`);
};

const logSaveFailure = (stage: string, error?: unknown) => {
  const stripeError = error as Partial<StripeError> | undefined;
  logWarning(
    LOG_TAG,
    `Save card failed: ${stage}`,
    stripeError?.code || stripeError?.type
      ? {
          code: stripeError.code ?? 'unknown',
          type: stripeError.type ?? 'unknown',
        }
      : undefined
  );
};

export interface IManageStripeAction extends IBaseAction {
  type: 'payment_stripe_pi_manage';

  data: {
    currency: string;
    publishableKey: string;
    // Unlike payment, this action has no fallback without a Customer Session - saving/managing
    // a card without Stripe's own consent/redisplay tracking is the exact problem it exists to
    // avoid, so it's required rather than optional here.
    customerSessionClientSecret: string;
    attemptUrl: string;
    paymentMethodTypes?: string[];
  };
}

interface ISetupIntentAttemptResult {
  setupIntentId: string;
  intentStatus: string;
  clientSecret: string;
  finalizeUrl: string;
}

// Saves/confirms a card via a SetupIntent - unlike usePaymentStripePayAction, nothing charges
// here, so on success this just reloads the underlying screen to reflect the change instead of
// continuing a booking/payment flow.
const useManageStripeAction = () => {
  const overlay = useOverlay();
  const language = useLanguage();

  const presentStripeCardError = (stage: string) => {
    logSaveFailure(stage);
    overlay.presentBasicAlert({
      title: language.get('error'),
      message: language.get('stripe_generic_error'),
      buttonTitle: language.get('close'),
    });
  };

  const handleManageStripeAction = async (
    action: IManageStripeAction
  ): Promise<IAction> => {
    const stripe = await loadStripe(action.data.publishableKey, {
      locale: language.getCurrentLanguage() as StripeElementLocale,
    });

    return new Promise((resolve, reject) => {
      if (!stripe) {
        presentStripeCardError('stripe-not-loaded');
        reject();
        return;
      }

      const resolveReload = () => resolve({ type: 'reload' } as IReloadAction);

      const rejectWithLog = (stage: string, error?: unknown) => {
        logSaveFailure(stage, error);
        reject(error);
      };

      const finalizeSetupIntent = (
        finalizeUrl: string,
        setupIntentId: string
      ) => {
        request<ISetupIntentAttemptResult>(finalizeUrl, {
          method: 'POST',
          body: {
            setup_intent_id: setupIntentId,
          },
        })
          .then((result) => {
            if (result.intentStatus === 'succeeded') {
              logSaveSuccess('finalized-after-3ds');
              resolveReload();
            } else {
              presentStripeCardError('finalize-not-succeeded');
            }
          })
          .catch((error) => rejectWithLog('finalize-request-failed', error));
      };

      const retrieveSetupIntent = (
        clientSecret: string,
        finalizeUrl: string,
        setupIntentId: string
      ) => {
        stripe
          .retrieveSetupIntent(clientSecret)
          .then(({ error: retrieveError, setupIntent }) => {
            if (!setupIntent) {
              rejectWithLog('retrieve-setup-intent-failed', retrieveError);
              return;
            }
            switch (setupIntent.status as string) {
              case 'succeeded':
                logSaveSuccess('succeeded-after-retrieve');
                resolveReload();
                break;

              case 'requires_action':
                // handleNextAction shows the modern 3DS2 modal challenge itself
                // (next_action.type 'use_stripe_sdk'), the common case for card SetupIntents.
                // redirect_to_url next actions require Stripe.js to navigate the top-level page
                // when a bank's own authentication page can't be embedded, which would strand
                // the patient on stripe-redirect.html with no SPA left to resume into (unlike
                // useStripeAttemptPayment's payment flow, which handles that case itself in a
                // controlled iframe). Bail out visibly rather than risk that silent dead end.
                if (setupIntent.next_action?.type === 'redirect_to_url') {
                  presentStripeCardError('redirect-based-3ds-not-supported');
                  break;
                }
                stripe
                  .handleNextAction({ clientSecret })
                  .then(({ error }) => {
                    if (error) {
                      rejectWithLog('handle-next-action-failed', error);
                      return;
                    }
                    finalizeSetupIntent(finalizeUrl, setupIntentId);
                  })
                  .catch((error) =>
                    rejectWithLog('handle-next-action-exception', error)
                  );
                break;

              // 'canceled' (the SetupIntent itself, not the sheet - Stripe apps show the same
              // generic error for this, e.g. Android's StripeIntent.Status.Canceled handling),
              // requires_payment_method after a failed or canceled authentication, or any other
              // terminal state - the overlay is already dismissed at this point, so this is the
              // only chance to tell the patient the card wasn't saved.
              case 'canceled':
              default:
                presentStripeCardError('retrieve-setup-intent-terminal-status');
                break;
            }
          })
          .catch((error) =>
            rejectWithLog('retrieve-setup-intent-exception', error)
          );
      };

      overlay.presentManageStripeCard({
        stripe,
        stripeParameters: action.data,
        onError: (error) => {
          rejectWithLog('stripe-form-error', error);
        },
        // Cancelling still reloads: Stripe's own "remove saved card" affordance inside the
        // sheet removes a card immediately against Stripe, independently of Save, so the
        // checkout screen needs to refetch to pick up any such removal either way.
        onCancel: () => {
          resolveReload();
        },
        onConfirmationToken: (confirmationToken: ConfirmationToken) => {
          request<ISetupIntentAttemptResult>(action.data.attemptUrl, {
            method: 'POST',
            body: {
              confirmation_token: confirmationToken.id,
            },
          })
            .then((attemptResult) => {
              switch (attemptResult.intentStatus) {
                case 'succeeded':
                  logSaveSuccess('succeeded-on-attempt');
                  resolveReload();
                  break;
                case 'requires_action':
                  retrieveSetupIntent(
                    attemptResult.clientSecret,
                    attemptResult.finalizeUrl,
                    attemptResult.setupIntentId
                  );
                  break;
                // requires_payment_method or any other terminal state - the overlay is already
                // dismissed at this point, so this is the only chance to tell the patient the
                // card wasn't saved.
                default:
                  presentStripeCardError('attempt-terminal-status');
                  break;
              }
            })
            .catch((error) => rejectWithLog('attempt-request-failed', error));
        },
      });
    });
  };

  return handleManageStripeAction;
};

export default useManageStripeAction;
